import { motion } from "motion/react";
import { LuFile, LuPlus, LuX } from "react-icons/lu";
import DocumentLink from "../../../../../../../../../../components/DocumentLink";
import Checkbox from "../../../../../../../../../../components/Checkbox";
import DragDropFileUpload from "../../../../../../../../../../components/DragDropFileUpload";
import type { DocumentMetadata, DocumentError } from "../../types";
import type { ParticipantOption } from "../ParticipantsSelectInput";
import ParticipantsSelect from "../ParticipantsSelect";
import { InformationCircleIcon } from "@heroicons/react/24/outline";

interface DocumentsSectionProps {
  displayDocuments: boolean;
  setDisplayDocuments: (display: boolean) => void;
  documents: DocumentMetadata[];
  setDocuments: (documents: DocumentMetadata[]) => void;
  documentErrors: DocumentError[];
  setDocumentErrors: (errors: DocumentError[]) => void;
  participantOptions?: ParticipantOption[];
}

export default function DocumentsSection({
  displayDocuments,
  setDisplayDocuments,
  documents,
  setDocuments,
  documentErrors,
  setDocumentErrors,
  participantOptions,
}: DocumentsSectionProps) {
  const addDocument = () => {
    setDocuments([
      ...documents,
      {
        title: "",
        description: "",
        file: null,
        bucketKey: null,
        isPublic: true,
        mimeType: "",
        selectedParticipants: [],
      },
    ]);
    setDocumentErrors([...documentErrors, {}]);
  };

  const removeDocument = (index: number) => {
    const newDocuments = [...documents];
    const newErrors = [...documentErrors];
    newDocuments.splice(index, 1);
    newErrors.splice(index, 1);
    setDocuments(newDocuments);
    setDocumentErrors(newErrors);
  };

  const updateDocument = (
    index: number,
    field:
      | "title"
      | "description"
      | "file"
      | "isPublic"
      | "selectedParticipants",
    value: string | File | boolean | ParticipantOption[]
  ) => {
    const newDocuments = [...documents];
    if (field === "file") {
      newDocuments[index][field] = value as File;
      newDocuments[index].mimeType = (value as File).type;
      // Clear file error when a file is added
      const newErrors = [...documentErrors];
      if (newErrors[index]) {
        newErrors[index] = { ...newErrors[index], file: undefined };
        setDocumentErrors(newErrors);
      }
    } else if (field === "title") {
      newDocuments[index][field] = value as string;
      // Clear title error when title is added
      const newErrors = [...documentErrors];
      if (newErrors[index]) {
        newErrors[index] = { ...newErrors[index], title: undefined };
        setDocumentErrors(newErrors);
      }
    } else if (field === "isPublic") {
      newDocuments[index][field] = value as boolean;
    } else if (field === "selectedParticipants") {
      newDocuments[index][field] = value as ParticipantOption[];
    } else {
      newDocuments[index][field] = value as string;
    }
    setDocuments(newDocuments);
  };

  const documentTitleField = (index: number, doc: DocumentMetadata) => {
    return (
      <div>
        <label className="block text-sm font-medium mb-1">Titre *</label>
        <input
          type="text"
          value={doc.title}
          onChange={(e) => updateDocument(index, "title", e.target.value)}
          className={`w-full p-2 rounded-md bg-input focus:outline-none focus:ring-2 focus:ring-card border ${
            documentErrors[index]?.title ? "border-red-500" : "border-border"
          }`}
          placeholder="Titre du document"
        />
        {documentErrors[index]?.title && (
          <p className="text-red-500 text-sm mt-1">
            {documentErrors[index].title}
          </p>
        )}
      </div>
    );
  };

  const documentDescriptionField = (index: number, doc: DocumentMetadata) => {
    return (
      <div>
        <label className="block text-sm font-medium mb-1">
          Description du document
        </label>
        <textarea
          value={doc.description}
          onChange={(e) => updateDocument(index, "description", e.target.value)}
          rows={2}
          className="w-full p-2 rounded-md bg-input focus:outline-none focus:ring-2 focus:ring-card border border-border"
          placeholder="Description du document"
        />
      </div>
    );
  };

  const documentUploadField = (index: number, doc: DocumentMetadata) => {
    const handleFilesSelect = (files: File[]) => {
      updateDocument(index, "file", files[0]);
    };

    return (
      <div>
        <DragDropFileUpload
          onFilesSelect={handleFilesSelect}
          selectedFile={doc.file}
          placeholder="Glissez-déposez un document ici ou cliquez pour sélectionner"
          maxFileSize={50 * 1024 * 1024} // 50MB
          className={documentErrors[index]?.file ? "border-red-500" : ""}
        />
      </div>
    );
  };

  if (displayDocuments) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 10 }}
        transition={{ duration: 0.3 }}
        className="border border-tertiary/20 rounded-md p-4"
      >
        <div className="flex flex-col gap-4">
          <div className="text-lg font-medium">Documents</div>

          {documents.length > 0 ? (
            <div className="space-y-4">
              {documents.map((doc, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  transition={{ duration: 0.3 }}
                  className="p-4 border border-border rounded-md bg-card/30"
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="text-sm font-medium">
                      Document {index + 1}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeDocument(index)}
                      className="text-tertiary hover:text-primary transition-colors cursor-pointer"
                    >
                      <LuX className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="flex flex-col gap-2">
                    {documentTitleField(index, doc)}

                    {documentDescriptionField(index, doc)}

                    {doc.bucketKey ? (
                      <DocumentLink bucketKey={doc.bucketKey} />
                    ) : (
                      documentUploadField(index, doc)
                    )}
                    {documentErrors[index]?.file && (
                      <p className="text-red-500 text-sm mt-1">
                        {documentErrors[index].file}
                      </p>
                    )}

                    <div
                      className="mt-2 flex items-center gap-2 cursor-pointer text-secondary hover:text-primary transition-all duration-300"
                      onClick={() => {
                        updateDocument(index, "isPublic", !doc.isPublic);
                      }}
                    >
                      <Checkbox checked={doc.isPublic} onChange={() => {}} />
                      <div className="text-sm">
                        Partager par défaut avec tous les participants de
                        l'activité
                      </div>
                    </div>

                    {!doc.isPublic ? (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.3 }}
                      >
                        {participantOptions && participantOptions.length > 0 ? (
                          <ParticipantsSelect
                            placeholder="Sélectionner une ou plusieurs personnes"
                            value={doc.selectedParticipants}
                            onChange={(value) =>
                              updateDocument(
                                index,
                                "selectedParticipants",
                                value
                              )
                            }
                            error={undefined}
                            participantOptions={participantOptions || []}
                          />
                        ) : (
                          <div className="text-sm text-accent flex items-center gap-1 mt-4">
                            <InformationCircleIcon className="w-4 h-4" />
                            Ajoutez des participants pour pouvoir partager ce
                            document
                          </div>
                        )}
                      </motion.div>
                    ) : null}
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <p className="text-secondary text-sm">Aucun document ajouté</p>
          )}

          <button
            type="button"
            onClick={addDocument}
            className="flex items-center gap-2 text-secondary hover:text-primary/80 transition-all duration-300 w-fit cursor-pointer"
          >
            <LuPlus className="w-4 h-4" />
            Ajouter un document
          </button>
        </div>
      </motion.div>
    );
  }

  return (
    <div
      className="flex items-center gap-2 cursor-pointer text-secondary hover:text-primary/80 transition-all duration-300"
      onClick={() => {
        setDisplayDocuments(true);
        // Add an empty document when the section is first opened
        if (documents.length === 0) {
          addDocument();
        }
      }}
    >
      <LuFile className="w-4 h-4" />
      Ajouter des <span className="underline">documents</span>
    </div>
  );
}
